import { useState, useMemo } from 'react';
import {
  Home, Dices, ShoppingBag, ListChecks, Trophy, Crown, Gift, Bell, Megaphone,
  MessagesSquare, Wallet, ArrowDownCircle, ArrowUpCircle, Play, Info, Sparkles,
  ChevronRight, ChevronLeft, CircleUser, Flame, Zap, Star, Lock, CheckCircle2,
  TrendingUp, Search, Filter, Smartphone,
} from 'lucide-react';
import PlayerHomePreview from './PlayerHomePreview';

/**
 * Multi-page live preview panel for the Theme Editor.
 *
 * Switches between mocks of the player app's main pages — Home, Roulette,
 * Marketplace, Tasks, Tiers, Referral, Wallet, Profile — so admins can verify
 * theme tokens recolor every surface they care about. Every page mock is
 * wrapped inside a `<div className="player-app">` that carries the inline
 * `--vl-*` CSS-variable map derived from the current draft.
 */
const TABS = [
  { id: 'home',        label: 'Home',        icon: Home },
  { id: 'roulette',    label: 'Roulette',    icon: Dices },
  { id: 'marketplace', label: 'Market',      icon: ShoppingBag },
  { id: 'tasks',       label: 'Tasks',       icon: ListChecks },
  { id: 'tiers',       label: 'Tiers',       icon: Crown },
  { id: 'referral',    label: 'Referral',    icon: Gift },
  { id: 'wallet',      label: 'Wallet',      icon: Wallet },
  { id: 'profile',     label: 'Profile',     icon: CircleUser },
];

export default function LivePreviewPanel({ theme }) {
  const [tab, setTab] = useState('home');

  return (
    <div className="space-y-2" data-testid="live-preview-panel">
      <div className="flex items-center gap-2">
        <Smartphone className="w-4 h-4 text-emerald-400" />
        <span className="text-xs font-bold uppercase tracking-wider text-white/80">Player Live Preview</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider px-2 py-0.5 rounded"
          style={{ background: 'rgba(34,255,136,0.12)', color: '#22FF88', border: '1px solid rgba(34,255,136,0.35)' }}>
          Updating live
        </span>
      </div>

      {/* Tab strip */}
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1" data-testid="preview-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition"
            style={{
              background: tab === t.id ? 'rgba(34,255,136,0.18)' : 'rgba(255,255,255,0.04)',
              color: tab === t.id ? '#22FF88' : 'rgba(255,255,255,0.6)',
              border: tab === t.id ? '1px solid rgba(34,255,136,0.4)' : '1px solid rgba(255,255,255,0.06)',
            }}
            data-testid={`preview-tab-${t.id}`}
          >
            <t.icon className="w-3 h-3" />
            {t.label}
          </button>
        ))}
      </div>

      <div className="w-full" style={{ maxWidth: '380px' }}>
        {tab === 'home'        && <PlayerHomePreview theme={theme} />}
        {tab === 'roulette'    && <RoulettePreview theme={theme} />}
        {tab === 'marketplace' && <MarketplacePreview theme={theme} />}
        {tab === 'tasks'       && <TasksPreview theme={theme} />}
        {tab === 'tiers'       && <TiersPreview theme={theme} />}
        {tab === 'referral'    && <ReferralPreview theme={theme} />}
        {tab === 'wallet'      && <WalletPreview theme={theme} />}
        {tab === 'profile'     && <ProfilePreview theme={theme} />}
      </div>
    </div>
  );
}

/* ---- shared chrome wrapper ---- */
function PageShell({ theme, title, accent, children, height = 760 }) {
  const t = theme || {};
  const cssVars = useMemo(() => {
    const out = {};
    if (!t) return out;
    for (const [k, v] of Object.entries(t)) {
      if (typeof v !== 'string' || !v.length) continue;
      if (['custom_css', 'logo_url', 'intro_logo_url', 'brand_name', 'tagline'].includes(k)) continue;
      out['--vl-' + k.replace(/_/g, '-')] = v;
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);
  return (
    <div
      className="player-app rounded-3xl overflow-hidden border border-white/10 select-none"
      style={{
        ...cssVars,
        background: t.bg || '#000000',
        color: t.text_primary || '#fff',
        height: `${height}px`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="px-4 py-3 flex items-center gap-3 border-b border-white/5"
        style={{ background: t.bg || '#000000' }}>
        <button className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.4)' }}>
          <ChevronLeft className="w-4 h-4" style={{ color: '#A78BFA' }} />
        </button>
        <div className="flex-1">
          <p className="display text-base font-extrabold uppercase tracking-wide" style={{ color: t.text_primary || '#fff' }}>{title}</p>
        </div>
        <span className="w-2 h-2 rounded-full" style={{ background: accent || t.neon || '#22FF88', boxShadow: `0 0 8px ${accent || t.neon || '#22FF88'}` }} />
      </div>
      <div className="flex-1 overflow-y-auto p-4" style={{ background: t.bg || '#000000' }}>
        {children}
      </div>
    </div>
  );
}

/* ---- ROULETTE ---- */
function RoulettePreview({ theme }) {
  const t = theme || {};
  const accent = t.page_roulette_accent || '#EF4444';
  return (
    <PageShell theme={theme} title="Roulette" accent={accent}>
      <div className="text-center mb-3">
        <p className="display text-lg font-extrabold" style={{ color: '#fff' }}>Spin & Win</p>
        <p className="text-xs" style={{ color: t.text_muted || 'rgba(255,255,255,0.65)' }}>Free daily spin · 1 left today</p>
      </div>

      {/* Wheel mock */}
      <div className="relative mx-auto w-56 h-56 rounded-full mb-5"
        style={{
          background: `conic-gradient(${accent} 0 60deg, #FFD93D 60deg 120deg, ${t.neon || '#22FF88'} 120deg 180deg, #5B2BFF 180deg 240deg, #EC4899 240deg 300deg, #38BDF8 300deg 360deg)`,
          boxShadow: `0 0 0 4px rgba(255,255,255,0.1), 0 0 30px ${accent}66`,
        }}>
        <div className="absolute inset-3 rounded-full"
          style={{ background: t.bg || '#000', border: '2px solid rgba(255,255,255,0.18)' }}>
          <div className="h-full flex items-center justify-center">
            <span className="display text-2xl font-black" style={{ color: accent }}>500</span>
          </div>
        </div>
        {/* Pointer */}
        <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-0 h-0"
          style={{ borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '14px solid #fff', filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }} />
      </div>

      <button className="w-full rounded-xl py-3 display font-extrabold text-base"
        style={{
          background: `linear-gradient(180deg, ${accent}, #B91C1C)`,
          color: '#fff',
          border: '2px solid #fff',
          boxShadow: `0 0 0 1px rgba(255,255,255,0.5), 0 10px 26px ${accent}66`,
          letterSpacing: '0.06em',
        }}>
        SPIN THE WHEEL
      </button>

      <div className="grid grid-cols-3 gap-2 mt-4">
        {['100 FP', '500 FP', '1000 FP', 'Free Spin', 'Cash $1', 'Mystery'].map((p) => (
          <div key={p} className="rounded-lg p-2 text-center"
            style={{ background: `${accent}12`, border: `1px solid ${accent}40` }}>
            <p className="text-[11px] font-bold" style={{ color: accent }}>{p}</p>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

/* ---- MARKETPLACE ---- */
function MarketplacePreview({ theme }) {
  const t = theme || {};
  const accent = t.page_marketplace_accent || '#F59E0B';
  const items = [
    { name: '$5 Cash',      cost: 1500, rarity: 'common' },
    { name: '$10 Cash',     cost: 2800, rarity: 'common' },
    { name: 'Free Spin x5', cost: 800,  rarity: 'rare' },
    { name: 'Mystery Box',  cost: 3500, rarity: 'epic' },
  ];
  return (
    <PageShell theme={theme} title="Marketplace" accent={accent}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `linear-gradient(135deg, #FBBF24, ${accent})` }}>
          <ShoppingBag className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="display text-base font-extrabold" style={{ color: '#fff' }}>Exchange your FP</p>
          <p className="text-[11px]" style={{ color: t.text_muted || 'rgba(255,255,255,0.6)' }}>You have <span style={{ color: accent, fontWeight: 700 }}>2,450 FP</span></p>
        </div>
      </div>

      <div className="rounded-xl p-2 mb-3 flex items-center gap-2"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <Search className="w-3.5 h-3.5 text-white/50" />
        <span className="text-[11px] text-white/50">Search items…</span>
        <Filter className="w-3.5 h-3.5 text-white/50 ml-auto" />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {items.map((i) => (
          <div key={i.name} className="rounded-xl p-3"
            style={{
              background: i.rarity === 'epic' ? `${accent}20` : 'rgba(255,255,255,0.03)',
              border: `1px solid ${i.rarity === 'epic' ? accent : 'rgba(255,255,255,0.08)'}`,
            }}>
            <div className="aspect-square rounded-lg mb-2 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, #FBBF24, ${accent})` }}>
              <Gift className="w-7 h-7 text-white" />
            </div>
            <p className="display font-bold text-xs text-white">{i.name}</p>
            <p className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: accent, fontWeight: 700 }}>{i.cost.toLocaleString()} FP</p>
            <button className="w-full mt-2 rounded-md py-1 text-[10px] font-bold"
              style={{ background: `linear-gradient(180deg, ${accent}, #B45309)`, color: '#fff' }}>
              Redeem
            </button>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

/* ---- TASKS ---- */
function TasksPreview({ theme }) {
  const t = theme || {};
  const accent = t.page_tasks_accent || '#10B981';
  const tasks = [
    { name: 'Daily Login',     reward: '50 FP', progress: 100, status: 'done' },
    { name: 'Play 3 Games',    reward: '150 FP', progress: 66 },
    { name: 'Refer a Friend',  reward: '500 FP', progress: 0, status: 'locked' },
    { name: 'Spin the Wheel',  reward: '100 FP', progress: 100, status: 'done' },
  ];
  return (
    <PageShell theme={theme} title="Daily Tasks" accent={accent}>
      <div className="rounded-2xl p-4 mb-3"
        style={{ background: `linear-gradient(135deg, ${accent}25, ${accent}08)`, border: `1px solid ${accent}50` }}>
        <p className="text-[11px] uppercase tracking-wider font-bold" style={{ color: accent }}>Total Reward</p>
        <p className="display text-3xl font-extrabold mt-1" style={{ color: '#fff' }}>800 <span className="text-base" style={{ color: accent }}>FP</span></p>
        <p className="text-[11px] mt-1" style={{ color: t.text_muted || 'rgba(255,255,255,0.6)' }}>Reset in 8h 24m</p>
      </div>

      <div className="space-y-2">
        {tasks.map((task) => {
          const done = task.status === 'done';
          const locked = task.status === 'locked';
          return (
            <div key={task.name} className="rounded-xl p-3 flex items-center gap-3"
              style={{
                background: done ? `${accent}14` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${done ? `${accent}55` : 'rgba(255,255,255,0.06)'}`,
                opacity: locked ? 0.55 : 1,
              }}>
              <span className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ background: done ? `linear-gradient(180deg, ${accent}, #047857)` : 'rgba(255,255,255,0.06)' }}>
                {done ? <CheckCircle2 className="w-4 h-4 text-white" /> :
                 locked ? <Lock className="w-4 h-4 text-white/60" /> :
                 <Zap className="w-4 h-4" style={{ color: accent }} />}
              </span>
              <div className="flex-1 min-w-0">
                <p className="display font-bold text-xs text-white">{task.name}</p>
                <p className="text-[10px]" style={{ color: accent, fontWeight: 700 }}>{task.reward}</p>
                <div className="mt-1 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full rounded-full" style={{ width: `${task.progress}%`, background: `linear-gradient(90deg, ${accent}, ${t.neon || '#22FF88'})` }} />
                </div>
              </div>
              {done && <span className="text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded" style={{ background: accent, color: '#fff' }}>Claim</span>}
            </div>
          );
        })}
      </div>
    </PageShell>
  );
}

/* ---- TIERS ---- */
function TiersPreview({ theme }) {
  const t = theme || {};
  const accent = t.page_tiers_accent || '#8B5CF6';
  const tiers = [
    { name: 'Elite',     icon: '👑', xp: 50000, current: false },
    { name: 'Platinum',  icon: '✨', xp: 25000, current: false },
    { name: 'Diamond',   icon: '💎', xp: 10000, current: true },
    { name: 'Gold',      icon: '🥇', xp: 5000,  current: false },
    { name: 'Silver',    icon: '🥈', xp: 3000,  current: false },
  ];
  return (
    <PageShell theme={theme} title="My Tier" accent={accent}>
      <div className="rounded-2xl p-4 mb-3"
        style={{ background: `linear-gradient(135deg, ${accent}30, ${accent}10)`, border: `1px solid ${accent}55` }}>
        <div className="flex items-center gap-3">
          <span className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
            style={{ background: `linear-gradient(135deg, ${accent}, #6D28D9)`, boxShadow: `0 8px 24px ${accent}55` }}>💎</span>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#C4B5FD' }}>Current Tier</p>
            <p className="display text-2xl font-extrabold" style={{ color: '#fff' }}>Diamond</p>
            <p className="text-[10px]" style={{ color: t.text_muted || 'rgba(255,255,255,0.6)' }}>6,250 / 10,000 XP</p>
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div className="h-full rounded-full" style={{ width: '62%', background: `linear-gradient(90deg, ${accent}, #6D28D9)` }} />
        </div>
      </div>

      <div className="space-y-2">
        {tiers.map((tier) => (
          <div key={tier.name} className="rounded-xl p-3 flex items-center gap-3"
            style={{
              background: tier.current ? `${accent}20` : 'rgba(255,255,255,0.03)',
              border: tier.current ? `1.5px solid ${accent}` : '1px solid rgba(255,255,255,0.06)',
            }}>
            <span className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
              style={{ background: tier.current ? `linear-gradient(135deg, ${accent}, #6D28D9)` : 'rgba(255,255,255,0.06)' }}>{tier.icon}</span>
            <div className="flex-1">
              <p className="display font-bold text-sm text-white">{tier.name}</p>
              <p className="text-[10px]" style={{ color: t.text_muted || 'rgba(255,255,255,0.6)' }}>{tier.xp.toLocaleString()} XP</p>
            </div>
            {tier.current && <span className="text-[10px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded" style={{ background: '#FCD34D', color: '#3F2A00' }}>Current</span>}
          </div>
        ))}
      </div>
    </PageShell>
  );
}

/* ---- REFERRAL ---- */
function ReferralPreview({ theme }) {
  const t = theme || {};
  const accent = t.page_referral_accent || '#FF6B00';
  return (
    <PageShell theme={theme} title="Refer & Earn" accent={accent}>
      <div className="rounded-2xl p-4 mb-3 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, #FF1A1A 100%)`,
          boxShadow: `0 10px 30px ${accent}88`,
        }}>
        <Flame className="absolute top-2 right-2 w-12 h-12 text-white/15" />
        <p className="display text-xl font-extrabold text-white">Earn 500 FP</p>
        <p className="text-xs text-white/90 mt-1">For every friend who signs up & deposits</p>
        <button className="mt-3 w-full rounded-lg py-2 text-xs font-extrabold display bg-white"
          style={{ color: accent }}>
          Share my link
        </button>
      </div>

      <div className="rounded-xl p-3 mb-3"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>Your link</p>
        <p className="font-mono text-[11px] truncate text-white mt-1">teamlobby.example/r/PLAYER123</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Referred', val: '12' },
          { label: 'Active',   val: '7' },
          { label: 'Earned',   val: '3,500 FP' },
        ].map((s) => (
          <div key={s.label} className="rounded-lg p-2 text-center"
            style={{ background: `${accent}10`, border: `1px solid ${accent}40` }}>
            <p className="display font-extrabold text-sm" style={{ color: accent }}>{s.val}</p>
            <p className="text-[9px] uppercase tracking-wider mt-0.5" style={{ color: t.text_muted || 'rgba(255,255,255,0.6)' }}>{s.label}</p>
          </div>
        ))}
      </div>

      <p className="text-[11px] uppercase tracking-wider font-bold mt-4 mb-2" style={{ color: '#C4B5FD' }}>Milestones</p>
      <div className="space-y-1.5">
        {[
          { count: 1, reward: '50 FP × 2', done: true },
          { count: 5, reward: '500 FP', done: true },
          { count: 10, reward: '1,500 FP + Spin', done: false },
          { count: 25, reward: '5,000 FP + VIP', done: false },
        ].map((m) => (
          <div key={m.count} className="rounded-lg p-2 flex items-center gap-2"
            style={{
              background: m.done ? `${accent}14` : 'rgba(255,255,255,0.03)',
              border: m.done ? `1px solid ${accent}55` : '1px solid rgba(255,255,255,0.06)',
            }}>
            <span className="w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-extrabold"
              style={{ background: m.done ? accent : 'rgba(255,255,255,0.06)', color: m.done ? '#fff' : 'rgba(255,255,255,0.5)' }}>
              {m.count}
            </span>
            <p className="flex-1 text-[11px] text-white">{m.reward}</p>
            {m.done && <CheckCircle2 className="w-4 h-4" style={{ color: accent }} />}
          </div>
        ))}
      </div>
    </PageShell>
  );
}

/* ---- WALLET ---- */
function WalletPreview({ theme }) {
  const t = theme || {};
  return (
    <PageShell theme={theme} title="Wallet">
      <div className="rounded-2xl p-4 mb-3"
        style={{ background: 'linear-gradient(135deg, #DCFCE7, #F0FDF4)', border: '1px solid rgba(34,197,94,0.25)' }}>
        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#15803D' }}>Wallet Balance</p>
        <p className="display text-3xl font-extrabold mt-1" style={{ color: '#0B0F0C' }}>$24.50</p>
      </div>

      <div className="rounded-2xl p-4 mb-3"
        style={{ background: 'linear-gradient(135deg, #FEF3C7, #FFFBEB)', border: '1px solid rgba(250,204,21,0.45)' }}>
        <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#92400E' }}>Freeplay Credits</p>
        <p className="display text-3xl font-extrabold mt-1" style={{ color: '#0B0F0C' }}>2,450 <span className="text-sm" style={{ color: '#92400E' }}>FP</span></p>
        <p className="text-[10px] mt-0.5 font-bold" style={{ color: '#B45309' }}>Tap to exchange in Marketplace →</p>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button className="display rounded-xl py-3 px-3 flex items-center justify-center gap-1.5 font-extrabold text-sm"
          style={{
            background: `linear-gradient(180deg, ${t.btn_add_credits_from || '#5B2BFF'}, ${t.btn_add_credits_to || '#3614C9'})`,
            color: t.btn_add_credits_text || '#fff',
            boxShadow: `0 8px 20px ${t.btn_add_credits_from ? `${t.btn_add_credits_from}55` : 'rgba(91,43,255,0.4)'}`,
          }}>
          <ArrowDownCircle className="w-4 h-4" />
          Deposit
        </button>
        <button className="display rounded-xl py-3 px-3 flex items-center justify-center gap-1.5 font-extrabold text-sm"
          style={{
            background: `linear-gradient(180deg, ${t.btn_withdraw_from || '#FF8A3D'}, ${t.btn_withdraw_to || '#E63946'})`,
            color: t.btn_withdraw_text || '#fff',
            boxShadow: `0 8px 20px ${t.btn_withdraw_from ? `${t.btn_withdraw_from}55` : 'rgba(230,57,70,0.4)'}`,
          }}>
          <ArrowUpCircle className="w-4 h-4" />
          Withdraw
        </button>
      </div>

      <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: t.text_muted || 'rgba(255,255,255,0.6)' }}>Recent transactions</p>
      <div className="space-y-1.5">
        {[
          { kind: 'in',  text: 'Game win · Royal',     amount: '+$5.40',  color: t.neon || '#22FF88' },
          { kind: 'out', text: 'Add Credits · Juwa',   amount: '-$10.00', color: '#F87171' },
          { kind: 'in',  text: 'Referral bonus',        amount: '+$2.00',  color: t.neon || '#22FF88' },
        ].map((tx, i) => (
          <div key={i} className="rounded-lg p-2 flex items-center justify-between"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-md flex items-center justify-center"
                style={{ background: `${tx.color}15` }}>
                {tx.kind === 'in' ? <TrendingUp className="w-3.5 h-3.5" style={{ color: tx.color }} /> : <ArrowUpCircle className="w-3.5 h-3.5" style={{ color: tx.color }} />}
              </span>
              <p className="text-[11px] text-white">{tx.text}</p>
            </div>
            <span className="font-mono text-[11px] font-bold" style={{ color: tx.color }}>{tx.amount}</span>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

/* ---- PROFILE ---- */
function ProfilePreview({ theme }) {
  const t = theme || {};
  const accent = t.neon || '#22FF88';
  return (
    <PageShell theme={theme} title="My Profile" accent={accent}>
      <div className="text-center mb-4">
        <span className="inline-block w-24 h-24 rounded-full mb-3"
          style={{
            background: 'radial-gradient(circle at 30% 25%, #FFFFFF 0%, #A78BFA 35%, #7C3AED 75%, #4C1D95 100%)',
            border: '3px solid #FCD34D',
            boxShadow: '0 0 0 4px rgba(252,211,77,0.35), 0 0 30px rgba(167,139,250,0.55)',
          }} />
        <p className="display text-xl font-extrabold" style={{ color: '#fff' }}>Player Demo</p>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1"
          style={{ background: 'rgba(168,85,247,0.16)', color: '#C4B5FD', border: '1px solid rgba(168,85,247,0.4)' }}>
          ⏷ Member since Jan 2026
        </span>
      </div>

      <div className="rounded-2xl p-4 mb-3"
        style={{ background: 'linear-gradient(135deg, rgba(45,27,89,0.9), rgba(26,9,51,0.95))', border: '1px solid rgba(168,85,247,0.45)' }}>
        <div className="flex items-center gap-3">
          <span className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
            style={{ background: 'linear-gradient(135deg, #A78BFA, #6D28D9)' }}>💎</span>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#C4B5FD' }}>Tier</p>
            <p className="display text-2xl font-extrabold" style={{ color: '#fff' }}>Diamond</p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {[
          { label: 'Email', value: 'player@demo.com', icon: Bell },
          { label: 'Phone', value: 'Add to claim $5 FP', icon: Star },
          { label: 'Change Password', value: 'Update now', icon: Lock },
        ].map((row, i) => (
          <div key={i} className="rounded-xl p-3 flex items-center gap-3"
            style={{ background: 'rgba(168,85,247,0.04)', border: '1px solid rgba(168,85,247,0.35)' }}>
            <span className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #6D28D9, #4C1D95)' }}>
              <row.icon className="w-4 h-4 text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="display font-bold text-sm text-white">{row.label}</p>
              <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>{row.value}</p>
            </div>
            <ChevronRight className="w-4 h-4" style={{ color: '#A78BFA' }} />
          </div>
        ))}
      </div>
    </PageShell>
  );
}
